import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/marketing/SectionHeading";
import { WorkshopConstructionSequence } from "@/components/marketing/WorkshopConstructionSequence";
import { useTilt, Glare } from "@/components/marketing/Depth4D";
import { PanelAmbience } from "@/components/marketing/HeroCinematic";
import { STAGES, EXAMPLES } from "./workshopData";
import { StageCanvas } from "./StageCanvas";

const StageRail = ({ stage, setStage }) => {
  const { t } = useLang();
  return (
    <ol className="relative space-y-1" data-testid="workshop-stage-rail">
      <span aria-hidden className="absolute left-[15px] top-4 bottom-4 w-px bg-white/10" />
      <motion.span aria-hidden className="absolute left-[15px] top-4 w-px origin-top" style={{ background: "linear-gradient(180deg,#F6E3A6,#D4AF37)", height: "calc(100% - 2rem)" }}
        animate={{ scaleY: stage / (STAGES.length - 1) }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} />
      {STAGES.map((s, i) => (
        <li key={s.id}>
          <button type="button" onClick={() => setStage(i)} data-testid={`workshop-stage-${s.id}`}
            className={`relative w-full text-left flex gap-4 rounded-2xl px-0 py-3 transition-opacity duration-300 ${i === stage ? "opacity-100" : i < stage ? "opacity-80 hover:opacity-100" : "opacity-50 hover:opacity-90"}`}>
            <span className="relative z-[1] w-[31px] h-[31px] rounded-full shrink-0 flex items-center justify-center text-[11px] font-mono-lux transition-[background-color,border-color,color] duration-500"
              style={{ background: i <= stage ? "linear-gradient(160deg,#F6E3A6,#C9A145)" : "#16120B", color: i <= stage ? "#2A1F08" : "#D4AF37", border: "1px solid rgba(212,175,55,0.5)" }} data-no-translate>{i + 1}</span>
            <span>
              <span className="block text-[15px] text-[#F5EFE2]">{t(s.name)}</span>
              <span className="block text-[12.5px] text-white/55 leading-snug mt-0.5">{t(s.line)}</span>
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
};

const Composer = ({ ex, setEx, custom, setCustom, draft, setDraft, start }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-8" data-testid="workshop-composer">
      <div className="flex flex-wrap gap-2">
        {EXAMPLES.map((e, i) => (
          <button key={e.id} type="button" onClick={() => { setEx(i); setCustom(false); start(); }} data-testid={`workshop-example-${e.id}`}
            className={`rounded-full border px-4 py-2 text-[13px] transition-[background-color,border-color,color] duration-300 ${!custom && ex === i ? "bg-[#D4AF37] border-[#D4AF37] text-[#1A1408]" : "border-white/12 text-white/75 hover:border-[#D4AF37]/50"}`}>{t(e.title)}</button>
        ))}
      </div>
      <form className="flex-1 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { setCustom(true); start(); } }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("Or describe your own tool…")} data-testid="workshop-custom-input"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/12 rounded-full px-5 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#D4AF37]/70 transition-colors" />
        <button type="submit" disabled={!draft.trim()} data-testid="workshop-custom-submit"
          className="rounded-full px-5 text-sm font-medium text-[#1A1408] disabled:opacity-40 transition-opacity" style={{ background: "linear-gradient(100deg,#F6E3A6,#D4AF37)" }}>{t("Describe")}</button>
      </form>
    </div>
  );
};

export const WorkshopStudio = () => {
  const { t } = useLang();
  const [ex, setEx] = useState(0);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState("");
  const [stage, setStage] = useState(0);
  const tilt = useTilt(2.5);
  const last = stage === STAGES.length - 1;
  const prompt = custom ? draft.trim() : EXAMPLES[ex].prompt;

  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="workshop-studio">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10">
        <SectionHeading className="lg:col-span-7" testId="workshop-heading" eyebrow="ARI Workshop" lead="Describe a tool." accent="ARI builds it for your venue." />
        <p className="lg:col-span-5 text-white/65 text-base leading-relaxed">
          {t("Need a shift-swap flow, a waste log or a checklist? Describe it, and ARI takes it from idea to a live tool your team uses, with you approving each step. Pick an example or write your own, then walk through how it is built.")}
        </p>
      </div>
      <Composer ex={ex} setEx={setEx} custom={custom} setCustom={setCustom} draft={draft} setDraft={setDraft} start={() => setStage(1)} />
      {custom ? (
        <div data-testid="workshop-custom-illustration">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-white/60">{t("Here is how ARI turns a request like yours into a working tool, stage by stage.")}</p>
            <button type="button" onClick={() => { setCustom(false); setStage(0); }} data-testid="workshop-back-to-examples"
              className="text-[12.5px] text-[#D4AF37] hover:text-[#F6E3A6] transition-colors">{t("Back to the examples")}</button>
          </div>
          <div className="-mx-4 sm:-mx-8"><WorkshopConstructionSequence /></div>
        </div>
      ) : (
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-4"><StageRail stage={stage} setStage={setStage} /></div>
        <motion.div className="group relative lg:col-span-8 rounded-[28px] p-5 sm:p-8 overflow-hidden" style={tilt.style} {...tilt.handlers} data-testid="workshop-canvas">
          <Glare glare={tilt.glare} />
          <PanelAmbience testId="workshop-ambience" network={12} />
          <div aria-hidden className="absolute inset-0 z-[1] rounded-[28px] pointer-events-none" style={{ border: "1px solid rgba(212,175,55,0.2)" }} />
          <div aria-hidden className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(246,227,166,0.6), transparent)" }} />
          <div className="relative z-[2] min-h-[300px] flex flex-col">
            <p className="text-[10px] font-mono-lux tracking-[0.3em] uppercase text-[#D4AF37] mb-4"><span data-no-translate>{stage + 1}/5 · </span>{t(STAGES[stage].name)}</p>
            <div className="flex-1"><StageCanvas stage={stage} ex={EXAMPLES[ex]} custom={custom} prompt={prompt} /></div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-white/[0.07] pt-5">
              <p className="text-[13px] text-white/60 leading-relaxed max-w-md" data-testid="workshop-stage-explain">{t(STAGES[stage].explain)}</p>
              <button type="button" data-testid="workshop-next"
                onClick={() => setStage(last ? 0 : stage + 1)}
                className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-[#1A1408] transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: "linear-gradient(100deg,#F6E3A6,#D4AF37 55%,#E9CE7A)", boxShadow: "0 10px 30px -10px rgba(212,175,55,0.6)" }}>
                {t(last ? "Start again" : "Next step")}<ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
      )}
      <p className="mt-6 text-[12px] text-white/45 max-w-3xl" data-testid="workshop-footnote">
        {t("Behind these five steps, ARI works through eight stages, including discovery, a build plan and a final review. Only the Owner, or someone they authorise, can build and publish. Examples are illustrative.")}
      </p>
    </section>
  );
};

export default WorkshopStudio;
